import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { ImageScaleView, ImageScaleViewOpts } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { Feature } from 'src/replicant/analytics';
import {
  FlexBoxImageScaleView,
  FlexBoxView,
} from '@play-co/timestep-core/flexbox';
import { getMatch2AssetPath } from '../../game/util/assets';
import { getPowerBlockMap } from '../../game/defs/powerBlocks';

import { AssetGroup } from '@play-co/timestep-core';

const ui = uiConfig.buruburu.care.confirmation;

export default class PopupTeamCompetitionInfo extends PopupBasic {
  constructor(opts: { superview: View; close: () => void }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      ...ui.popup,
      height: 1050,
      darkerBg: true,
      tracking: {
        feature: Feature.MATCH2.TEAM.COMPETITION._,
        $subFeature: Feature.MATCH2.TEAM.COMPETITION.INFO_POPUP,
      },
    });

    this.buttonClose.removeFromSuperview();
    this.message.removeFromSuperview();
    this.title.updateOpts({ size: 35 });

    const flexView = new FlexBoxView({
      superview: this.box,
      width: this.box.style.width,
      height: this.box.style.height,
      flex: {
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 50,
      },
    });

    this.createPanel1(flexView);
    this.createArrow(flexView);
    this.createPanel2(flexView);

    new LangBitmapFontTextView({
      superview: flexView,
      size: 27,
      color: '#BF8147',
      font: 'Body',
      localeText: () => i18n('match2.team.competition_tutorial_3'),
      width: this.box.style.width * 0.85,
      height: 100,
      align: 'center',
      verticalAlign: 'center',
      x: this.box.style.width * 0.5,
      y: 860,
      wordWrap: true,
    });

    new ButtonScaleViewWithText({
      ...ui.okButton,
      superview: flexView,
      localeText: () => i18n('basic.continue'),
      onClick: async () => {
        sounds.sfx.play('ok');
        opts.close();
      },
      tracking: {
        feature: Feature.MATCH2.TEAM.COMPETITION._,
        $subFeature: Feature.MATCH2.TEAM.COMPETITION.INFO_POPUP_CONTINUE,
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/match2/common/tutorial_arrow.png',
      'assets/ui/match2/common/panel_pinch.png',
      'assets/ui/match2/team/competition/tutorial_mug.png',
      'assets/ui/match2/common/tutorial_stars.png',
      'assets/ui/match2/common/fx_shine.png',
      'assets/ui/shared/icons/gem.png',
      'assets/ui/match2/team/competition/bag.png',
    ]);
  }

  private createArrow(superview: View) {
    return new ImageScaleView({
      superview,
      image: 'assets/ui/match2/common/tutorial_arrow.png',
      width: 52,
      height: 65,
    });
  }

  private createSubPanel(
    superview: View,
    subTitle: string,
    opts?: ImageScaleViewOpts,
  ): FlexBoxImageScaleView {
    const subPanel = new FlexBoxImageScaleView({
      superview,
      width: 489,
      height: 160,
      x: this.box.style.width * 0.5,
      ...opts,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 126, right: 126 },
        vertical: { top: 106, bottom: 106 },
      },
      image: 'assets/ui/match2/common/panel_pinch.png',
      flex: {
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      },
    });

    new LangBitmapFontTextView({
      superview: subPanel,
      size: 29,
      color: '#BF8147',
      font: 'Body',
      localeText: () => i18n(subTitle),
      width: 440,
      height: 100,
      align: 'center',
      verticalAlign: 'center',
      wordWrap: true,
    });

    return subPanel;
  }

  private createPanel1(superview: View) {
    const subPanel = this.createSubPanel(
      superview,
      'match2.team.competition_tutorial_1',
      { height: 362 },
    );

    new ImageScaleView({
      superview: subPanel,
      image: 'assets/ui/match2/team/competition/tutorial_mug.png',
      width: 183,
      height: 89,
    });

    new ImageScaleView({
      superview: subPanel,
      image: 'assets/ui/match2/common/tutorial_stars.png',
      width: 361,
      height: 126,
    });

    return subPanel;
  }

  private createPanel2(superview: View) {
    const subPanel = this.createSubPanel(
      superview,
      'match2.team.competition_tutorial_2',
      {
        height: 283,
      },
    );

    const panel = new View({
      superview: subPanel,
      width: 356,
      height: 177,
      x: subPanel.style.width * 0.5,
      y: 135,
    });

    new ImageScaleView({
      superview: panel,
      image: 'assets/ui/match2/common/fx_shine.png',
      width: 177,
      height: 177,
      centerOnOrigin: true,
      x: panel.style.width * 0.35,
      y: panel.style.height * 0.5,
    });

    new ImageScaleView({
      superview: panel,
      image: 'assets/ui/match2/common/fx_shine.png',
      width: 177,
      height: 177,
      centerOnOrigin: true,
      x: panel.style.width * 0.65,
      y: panel.style.height * 0.5,
    });

    new ImageScaleView({
      superview: panel,
      image: 'assets/ui/shared/icons/gem.png',
      width: 90,
      height: 90,
      centerOnOrigin: true,
      x: panel.style.width * 0.75,
      y: panel.style.height * 0.6,
      scaleMethod: 'contain',
      scale: 1,
    });

    new ImageScaleView({
      superview: panel,
      image: getMatch2AssetPath(getPowerBlockMap().rocket),
      width: 109,
      height: 99,
      centerOnOrigin: true,
      x: panel.style.width * 0.7,
      y: panel.style.height * 0.33,
      scaleMethod: 'contain',
      scale: 0.7,
      r: -Math.PI / 8,
    });

    new ImageScaleView({
      superview: panel,
      image: getMatch2AssetPath(getPowerBlockMap().bomb),
      width: 90,
      height: 90,
      centerOnOrigin: true,
      x: panel.style.width * 0.3,
      y: panel.style.height * 0.33,
      scaleMethod: 'contain',
      scale: 0.7,
    });

    new ImageScaleView({
      superview: panel,
      image: 'assets/ui/shared/icons/gem.png',
      width: 90,
      height: 90,
      centerOnOrigin: true,
      x: panel.style.width * 0.25,
      y: panel.style.height * 0.6,
      scaleMethod: 'contain',
      scale: 0.7,
      flipX: true,
      r: -Math.PI / 10,
    });

    new ImageScaleView({
      superview: panel,
      image: 'assets/ui/match2/team/competition/bag.png',
      width: 356,
      height: 126,
      centerOnOrigin: true,
      x: panel.style.width * 0.5,
      y: panel.style.height * 0.5,
      scaleMethod: 'contain',
      scale: 1.5,
    });

    new ImageScaleView({
      superview: panel,
      image: getMatch2AssetPath('power/block.cube.all.png'),
      width: 88,
      height: 88,
      centerOnOrigin: true,
      x: panel.style.width * 0.47,
      y: panel.style.height * 0.65,
      scaleMethod: 'contain',
      scale: 0.7,
    });

    return subPanel;
  }

  public init() {
    super.init({ title: i18n('match2.battlePass.tutorial_title') });
  }
}
